import { captureException } from '@sentry/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useEffectEvent } from 'react'
import { useCurrentUser } from '~/hooks/users/useCurrentUser'
import { userPreferencesQueryOptions } from '~/queries/user'
import { getQueryKey } from '~/services'
import { setAccountPreference } from '~/services/preferences/userPreferences'

type Props = {
  children?: React.ReactNode
}

const PARTNERSTACK_SIGNUP_STATUS_PATH = 'partnerStackSignupStatus' as const
const PARTNERSTACK_SIGNUP_MAX_AGE_MS = 24 * 60 * 60 * 1000
const partnerStackHandlingUsers = new Set<string>()

export const PartnerStackProvider = ({ children }: Props) => {
  const user = useCurrentUser()
  const queryClient = useQueryClient()
  const signupStatusQuery = useQuery(userPreferencesQueryOptions({ path: PARTNERSTACK_SIGNUP_STATUS_PATH }))

  const setSignupStatus = useEffectEvent(async (outcome: 'reported' | 'suppressed-old-account') => {
    const nextStatus = {
      outcome,
      handledAt: new Date().toISOString(),
    } as const

    await setAccountPreference({ path: PARTNERSTACK_SIGNUP_STATUS_PATH, value: nextStatus })
    queryClient.setQueryData(getQueryKey('preferences/$path', { path: PARTNERSTACK_SIGNUP_STATUS_PATH }), nextStatus)

    return nextStatus
  })

  const handlePartnerStackSignup = useEffectEvent(async () => {
    if (!signupStatusQuery.isSuccess || signupStatusQuery.data || partnerStackHandlingUsers.has(user.id)) {
      return
    }

    partnerStackHandlingUsers.add(user.id)

    try {
      const accountAgeMs = Date.now() - new Date(user.createdAt).getTime()

      if (!Number.isFinite(accountAgeMs) || accountAgeMs > PARTNERSTACK_SIGNUP_MAX_AGE_MS) {
        await setSignupStatus('suppressed-old-account')
        return
      }

      const growsumo = window.growsumo
      if (!growsumo) {
        return
      }

      growsumo.data.name = 'anonymous'
      growsumo.data.email = user.email
      growsumo.data.customer_key = user.id
      growsumo.createSignup()
      await setSignupStatus('reported')
    } catch (error) {
      console.error('Failed to handle PartnerStack signup tracking', error)
      captureException(error)
    } finally {
      partnerStackHandlingUsers.delete(user.id)
    }
  })

  useEffect(() => {
    const previousGrowsumoInit = window.growsumoInit

    window.growsumoInit = () => {
      try {
        previousGrowsumoInit?.()
      } finally {
        void handlePartnerStackSignup()
      }
    }

    void handlePartnerStackSignup()

    return () => {
      window.growsumoInit = previousGrowsumoInit
    }
  }, [handlePartnerStackSignup])

  useEffect(() => {
    void handlePartnerStackSignup()
  }, [handlePartnerStackSignup, signupStatusQuery.isSuccess, signupStatusQuery.data, user.id])

  return <>{children}</>
}
